import { useEffect, useCallback } from 'react';

const API = '/api';

async function ensurePushSubscription() {
    // Check browser support
    if (!('serviceWorker' in navigator)) {
        return { ok: false, reason: 'Service Workers not supported' };
    }
    if (!('PushManager' in window)) {
        return { ok: false, reason: 'Push API not supported in this browser' };
    }
    if (!('Notification' in window)) {
        return { ok: false, reason: 'Notifications not supported' };
    }

    try {
        // Force service worker update
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
            await reg.update();
        }

        const reg = await navigator.serviceWorker.ready;

        // Check if already subscribed
        let sub = await reg.pushManager.getSubscription();
        if (sub) {
            await sendSubscriptionToServer(sub);
            return { ok: true, reason: 'Already subscribed' };
        }

        // Check current permission state
        const currentPermission = Notification.permission;
        if (currentPermission === 'denied') {
            return { ok: false, reason: 'Notifications are blocked. Please enable them in your browser/app settings.' };
        }

        // Request permission (must be in user gesture context)
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            return { ok: false, reason: `Notification permission ${permission}. Please allow notifications.` };
        }

        // Get VAPID key from env or server
        let vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        if (!vapidKey) {
            try {
                const resp = await fetch(`${API}/push/vapid-key`);
                const data = await resp.json();
                vapidKey = data.publicKey;
            } catch {
                return { ok: false, reason: 'Could not fetch push server key' };
            }
        }
        if (!vapidKey) {
            return { ok: false, reason: 'Push server key not configured' };
        }

        // Subscribe to push
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });

        await sendSubscriptionToServer(sub);
        return { ok: true, reason: 'Push notifications enabled' };
    } catch (err) {
        return { ok: false, reason: `Push setup error: ${err.message}` };
    }
}

export function usePushNotifications(user) {
    // Re-sync existing subscription on login (no permission prompt)
    const syncExisting = useCallback(async () => {
        if (!user) return;

        // ── FCM (Android TWA) - register the device's FCM token with our
        // backend so reminders fire reliably even when the TWA process is
        // killed by Android. The bridge is only available inside the TWA;
        // browser users keep using Web Push below.
        try {
            if (typeof window !== 'undefined' && window.BrainSyncFCM?.requestToken) {
                const onToken = async (event) => {
                    window.removeEventListener('brainsync-fcm-token', onToken);
                    const { ok, token } = event.detail || {};
                    if (!ok || !token) return;
                    try {
                        await fetch(`${API}/push/fcm-register`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ token, platform: 'android' }),
                        });
                        if (window.BrainSyncFCM.markRegistered) {
                            window.BrainSyncFCM.markRegistered();
                        }
                    } catch (_) {
                        // Non-fatal - we'll retry on the next login.
                    }
                };
                window.addEventListener('brainsync-fcm-token', onToken);
                window.BrainSyncFCM.requestToken();
            }
        } catch (_) {
            // FCM not available - fall through to web push.
        }

        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await sendSubscriptionToServer(sub);
            }
        } catch {
            // Non-fatal
        }
    }, [user]);

    useEffect(() => {
        syncExisting();
    }, [syncExisting]);

    return { ensurePushSubscription };
}

// Named export for direct import
export { ensurePushSubscription };

async function sendSubscriptionToServer(sub) {
    const subJson = sub.toJSON();
    await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            endpoint: subJson.endpoint,
            keys: subJson.keys,
        }),
    });
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}
